<script lang="ts" setup>
import { ref, Ref, computed, WritableComputedRef, PropType, watch } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    ConnectFlowAudioModel,
    VueFlowNodeModel,
} from '@/types';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import store from '@/store';
import { PlayIcon } from '@heroicons/vue/outline';
import { helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
});

const showForm = ref(false);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const audioOptions = computed(
    () => store.getters['flow/' + ConnectFlowModelingGetters.AUDIO_OPTIONS],
);
interface IAudioFormState {
    audio: null | ConnectFlowAudioModel;
    text: undefined | string;
}

const formState: Ref<IAudioFormState> = ref({
    audio: null,
    text: undefined,
});
const textValidation = t('connectFlow.node.playAudio.validation');
const atLeastOneFilled = helpers.withMessage(textValidation, (value, state) => {
    return !!state.audio || !!state.text;
});
const rules = computed<ValidationArgs<IAudioFormState>>(() => {
    const r: ValidationArgs<IAudioFormState> = {
        audio: {
            atLeastOneFilled,
        },
        text: {
            atLeastOneFilled,
        },
    };
    return r;
});
const v$: Ref<Validation<ValidationArgs<IAudioFormState>>> = useVuelidate(rules, formState);

const nodeText = computed(() => {
    let text = t('connectFlow.node.playAudio.empty');
    if (nodeWritable.value.data.related) {
        text = nodeWritable.value.data.related.label;
    } else if (nodeWritable.value.data.textToAudio) {
        text = t('connectFlow.node.playAudio.textToAudio');
    }
    return text;
});

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}
async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value) {
        if (formState.value.audio) {
            nodeWritable.value.data.related = {
                value: formState.value.audio.id,
                label: formState.value.audio.name,
            };
            nodeWritable.value.data.textToAudio = undefined;
        } else if (formState.value.text) {
            nodeWritable.value.data.textToAudio = formState.value.text;
            nodeWritable.value.data.related = null;
        }
        showForm.value = false;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    }
    v$.value.$reset();
}

watch([nodeWritable.value], () => {
    formState.value.audio = audioOptions.value.find(
        (audio: ConnectFlowAudioModel) => audio.id == nodeWritable.value.data.related?.value,
    );
    formState.value.text = nodeWritable.value.data.textToAudio;

    v$.value.$validate().then((result) => {
        isFormCorrect.value = result;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    });
});
</script>

<template>
    <UiNode
        :nodeId="node.id"
        :title="$t('connectFlow.node.playAudio.title')"
        :icon="PlayIcon"
        showActions
        @editNode="showForm = true"
        @removeNode="removeNode"
        :class="isFormCorrect ? '' : 'border-red-300 shadow-red-300'"
    >
        <div class="font-medium text-gray-600">
            <span>
                {{ nodeText }}
            </span>
        </div>
    </UiNode>
    <UiRightPanel
        v-model="showForm"
        :title="$t('connectFlow.node.playAudio.title')"
        :icon="PlayIcon"
    >
        <form autocomplete="off" class="flex flex-col gap-3" @submit.prevent="onSubmit">
            <div class="sm:col-span-12">
                <label class="mb-2 block text-sm font-medium text-gray-800">
                    {{ $t('connectFlow.node.playAudio.select') }}
                </label>
                <UiMultiselect
                    name="audio"
                    v-model="v$.audio.$model"
                    :options="audioOptions"
                    label="name"
                    value-prop="id"
                    :disabled="formState.text?.length > 0"
                    :errors="v$.audio.$silentErrors"
                    can-clear
                />
            </div>
            <UiTextArea
                :label="$t('connectFlow.node.playAudio.write')"
                name="text"
                v-model="v$.text.$model"
                :disabled="formState.audio?.name.length > 0"
                :errors="v$.text.$silentErrors"
            />
            <div class="mt-6 grid grid-cols-2 place-content-around gap-4 text-center">
                <UiButton variant="white" text-variant="gray-700" @click="showForm = false">
                    {{ $t('core.actions.Cancel') }}
                </UiButton>
                <UiButton type="submit" variant="primary" text-variant="white">
                    {{ $t('core.actions.Apply') }}
                </UiButton>
            </div>
        </form>
    </UiRightPanel>
</template>
